/**
 * features/admin/DataAudit.tsx
 * ------------------------------------------------------------------
 * "Data & Backups" — integrity checks + backup management.
 *
 *   1. Integrity — server-side, role-scoped data-consistency scan
 *      (runIntegrityCheck).
 *   2. Backups — domain-scoped downloads (runScopedBackup). Each admin
 *      sees only the scopes their role can access in-app:
 *        academicAdmin   -> Academic
 *        operationsAdmin -> Finance + HR
 *        systemAdmin/admin -> Academic + Finance + HR + Full system
 *      Plus automated-archive info and backup history. The previous
 *      client-side full export (which read collections in the browser
 *      and missed finance/HR) has been retired in favour of the
 *      server-side scoped path.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  ShieldAlert,
  Database,
  Cloud,
  HardDrive,
  Clock,
  RefreshCw,
  Shield,
  GraduationCap,
  Wallet,
  Users,
  ChevronDown,
  ChevronRight,
  Filter,
  FileDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { functions, db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import {
  GradientHero,
  SoftCard,
  AccentButton,
} from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';

type IssueLevel = 'warn' | 'error';
type Domain = 'academic' | 'finance' | 'hr';
type ScopeKey = 'academic' | 'finance' | 'hr' | 'system';

interface Issue {
  level: IssueLevel;
  domain: Domain | string;
  text: string;
  code?: string;
}

interface CheckFailure {
  ref: string;
  detail: string;
  /** Optional per-failure severity; falls back to the check's severity. */
  severity?: IssueLevel;
}

interface CheckResult {
  id: string;
  label: string;
  category: string; // 'Academic' | 'Finance' | 'HR'
  severity: IssueLevel;
  passed: boolean;
  scanned: number;
  failures: CheckFailure[];
}

interface IntegrityResult {
  ok: boolean;
  generatedAt: string;
  scopesRun: string[];
  counts: { errors: number; warnings: number; recordsScanned: number };
  /** Registry checks (structured, drill-downable). Optional for
   *  backward-compatibility with older function deployments. */
  checks?: CheckResult[];
  issues: Issue[];
}

interface ScopedBackupResult {
  ok: boolean;
  scope: string;
  totalRecords: number;
  sizeEstimate: string;
  storagePath: string;
  inlined: boolean;
  data: Record<string, unknown> | null;
}

interface BackupRecord {
  id: string;
  type: 'manual' | 'scheduled' | 'local' | 'scoped';
  scope?: string;
  status: 'completed' | 'failed' | 'in_progress';
  collections: number;
  totalRecords: number;
  sizeEstimate?: string;
  createdAt: string;
  createdBy?: string;
  storagePath?: string;
  error?: string;
}

type AuditTab = 'integrity' | 'backups';

const DOMAIN_LABEL: Record<string, string> = {
  academic: 'Academic',
  finance: 'Finance',
  hr: 'HR',
  drift: 'Drift',
  system: 'Full system',
};
const DOMAIN_BADGE: Record<string, string> = {
  academic: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  finance: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  hr: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  drift: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

/** Registry checks carry a capitalised category; map it to the domain
 *  key the badges/labels use. */
const DOMAIN_FROM_CAT: Record<string, string> = {
  Academic: 'academic',
  Finance: 'finance',
  HR: 'hr',
  Drift: 'drift',
};

const SCOPE_CARDS: { key: ScopeKey; icon: LucideIcon; desc: string }[] = [
  { key: 'academic', icon: GraduationCap, desc: 'Students, classes, marks, attendance, discipline, admissions, families and parent messages.' },
  { key: 'finance', icon: Wallet, desc: 'Invoices, payments, fee structures, discounts, academic holds and finance settings.' },
  { key: 'hr', icon: Users, desc: 'Staff, payroll, payslips, leave, contracts, gratuity and tax tables.' },
  { key: 'system', icon: HardDrive, desc: 'Every collection, including identity and shared system data. Administrators only.' },
];

function downloadJson(obj: unknown, filename: string) {
  const json = JSON.stringify(obj, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function downloadCsv(rows: string[][], filename: string) {
  const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function DataAudit() {
  const { userData } = useAuth();

  // Role resolution (mirrors resolveScopes; server still enforces).
  const roles: string[] =
    userData?.roles && userData.roles.length
      ? (userData.roles as string[])
      : userData?.role
        ? [userData.role as string]
        : [];
  const isFullAdmin = roles.includes('admin') || roles.includes('systemAdmin');

  // Integrity is System Administrator only — non-admins land on Backups.
  const [tab, setTab] = useState<AuditTab>(isFullAdmin ? 'integrity' : 'backups');
  const [saveModal, setSaveModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({ show: false, status: 'saving', message: '' });
  const [backupHistory, setBackupHistory] = useState<BackupRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Extra intraday-backup toggle (reportBackupSettings/config.intradayEnabled;
  // absent = enabled). Controls the 06:00–21:00 every-3h scheduled backups.
  const [intradayOn, setIntradayOn] = useState(true);
  const [savingIntraday, setSavingIntraday] = useState(false);
  const [downloadingScope, setDownloadingScope] = useState<ScopeKey | null>(null);

  // ── INTEGRITY (server-side) ───────────────────────────────
  const [runningIntegrity, setRunningIntegrity] = useState(false);
  const [integrity, setIntegrity] = useState<IntegrityResult | null>(null);
  const [catFilter, setCatFilter] = useState<'all' | 'Academic' | 'Finance' | 'HR' | 'Drift'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const runIntegrity = useCallback(async () => {
    setRunningIntegrity(true);
    try {
      const fn = httpsCallable(functions, 'runIntegrityCheck');
      const res = (await fn({})) as { data: IntegrityResult };
      setIntegrity(res.data);
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: `Integrity check failed: ${(e as Error).message}` });
    } finally {
      setRunningIntegrity(false);
    }
  }, []);

  // Auto-run once when the Integrity tab is first opened (admins only).
  useEffect(() => {
    if (tab === 'integrity' && isFullAdmin && !integrity && !runningIntegrity) {
      runIntegrity();
    }
  }, [tab, isFullAdmin, integrity, runningIntegrity, runIntegrity]);

  const errorCount = integrity?.counts.errors ?? 0;
  const warnCount = integrity?.counts.warnings ?? 0;
  const recordsScanned = integrity?.counts.recordsScanned ?? 0;
  const sortedIssues = integrity
    ? [...integrity.issues].sort((a, b) =>
        a.level === b.level ? 0 : a.level === 'error' ? -1 : 1)
    : [];

  // Registry checks (structured) vs legacy inline cross-reference issues.
  const checks = integrity?.checks ?? [];
  const checkIds = new Set(checks.map((c) => c.id));
  const visibleChecks =
    catFilter === 'all' ? checks : checks.filter((c) => c.category === catFilter);
  // Drop any flat issue that a registry check already emitted (the server
  // folds check failures into `issues` for back-compat) to avoid showing
  // the same failure twice.
  const legacyIssues = sortedIssues.filter((i) => !i.code || !checkIds.has(i.code));
  const filteredLegacy =
    catFilter === 'all'
      ? legacyIssues
      : legacyIssues.filter((i) => i.domain === DOMAIN_FROM_CAT[catFilter]);

  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  function exportFailuresCsv() {
    const rows: string[][] = [['check', 'category', 'severity', 'reference', 'detail']];
    for (const c of visibleChecks) {
      for (const f of c.failures) {
        rows.push([c.label, c.category, f.severity ?? c.severity, f.ref, f.detail]);
      }
    }
    downloadCsv(rows, `lkc-integrity-failures-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  // ── BACKUP SCOPES (mirrors resolveScopes; server still enforces) ──
  const allowedScopes: Record<ScopeKey, boolean> = {
    academic: isFullAdmin || roles.includes('academicAdmin'),
    finance: isFullAdmin || roles.includes('operationsAdmin'),
    hr: isFullAdmin || roles.includes('operationsAdmin'),
    system: isFullAdmin,
  };
  const visibleCards = SCOPE_CARDS.filter((c) => allowedScopes[c.key]);

  const loadBackupHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'reportBackupHistory'), orderBy('createdAt', 'desc'), limit(20)),
      );
      setBackupHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as BackupRecord));
    } catch {
      setBackupHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { if (tab === 'backups') loadBackupHistory(); }, [tab, loadBackupHistory]);

  const loadIntradaySetting = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'reportBackupSettings', 'config'));
      setIntradayOn(!(snap.exists() && snap.data()?.intradayEnabled === false));
    } catch {
      setIntradayOn(true);
    }
  }, []);
  useEffect(() => { if (tab === 'backups') loadIntradaySetting(); }, [tab, loadIntradaySetting]);

  async function toggleIntraday(next: boolean) {
    setSavingIntraday(true);
    setIntradayOn(next); // optimistic
    try {
      await setDoc(
        doc(db, 'reportBackupSettings', 'config'),
        { intradayEnabled: next, updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? null },
        { merge: true },
      );
    } catch (e) {
      setIntradayOn(!next); // revert
      setSaveModal({ show: true, status: 'error', message: `Could not update the intraday-backup toggle — ${(e as Error).message}` });
    } finally {
      setSavingIntraday(false);
    }
  }

  // Manual, admin-only prune of the OLDEST backups to free Storage. Never
  // automatic — always a deliberate click here.
  async function pruneOldest() {
    const input = window.prompt(
      'Delete how many of the OLDEST backups? This frees Storage space and cannot be undone.',
      '5',
    );
    if (input == null) return;
    const count = Math.floor(Number(input));
    if (!count || count < 1) { setSaveModal({ show: true, status: 'error', message: 'Enter a valid number of backups to delete.' }); return; }
    if (!window.confirm(`Permanently delete the ${count} oldest backup file(s)? Newer backups are kept.`)) return;
    setSaveModal({ show: true, status: 'saving', message: `Deleting the ${count} oldest backup(s)…` });
    try {
      const fn = httpsCallable(functions, 'pruneOldestBackups');
      const res = (await fn({ count })) as { data: { deleted: number; freedMb: string } };
      setSaveModal({ show: true, status: 'success', message: `Deleted ${res.data.deleted} backup(s) — freed ${res.data.freedMb}.` });
      loadBackupHistory();
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: `Could not prune backups — ${(e as Error).message || 'please retry.'}` });
    }
  }

  async function downloadScopedBackup(key: ScopeKey) {
    const label = DOMAIN_LABEL[key] ?? key;
    setDownloadingScope(key);
    setSaveModal({ show: true, status: 'saving', message: `Preparing ${label} backup…` });
    try {
      const fn = httpsCallable(functions, 'runScopedBackup');
      const res = (await fn({ scope: key })) as { data: ScopedBackupResult };
      const r = res.data;
      if (r.inlined && r.data) {
        downloadJson(r.data, `lkc-${key}-backup-${new Date().toISOString().slice(0, 10)}.json`);
        setSaveModal({ show: true, status: 'success', message: `${label} backup downloaded — ${r.totalRecords} records.` });
      } else {
        setSaveModal({ show: true, status: 'success', message: `${label} backup archived to Storage (${r.sizeEstimate}) — too large for direct download. Path: ${r.storagePath}` });
      }
      loadBackupHistory();
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: `Backup failed: ${(e as Error).message}` });
    } finally {
      setDownloadingScope(null);
    }
  }

  // ── RENDER ────────────────────────────────────────────────

  const tabs = [
    ...(isFullAdmin
      ? [{ id: 'integrity' as const, label: 'Integrity Checks', icon: Shield }]
      : []),
    { id: 'backups' as const, label: 'Backups', icon: Database },
  ];

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Database}
        title="Data & Backups"
        subtitle="Integrity checks, backup management, and data export"
      />

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? 'seg-on' : 'seg-off'
              }`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'integrity' && renderIntegrity()}
      {tab === 'backups' && renderBackups()}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );

  function renderIntegrity() {
    return (
      <>
        {/* Action row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            {integrity ? (
              <>
                <span>
                  Last run {new Date(integrity.generatedAt).toLocaleString('en-GB', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {integrity.scopesRun.map((s) => (
                  <span key={s} className={`px-2 py-0.5 rounded-full font-medium ${DOMAIN_BADGE[s] ?? 'bg-gray-100 text-gray-600'}`}>
                    {DOMAIN_LABEL[s] ?? s}
                  </span>
                ))}
              </>
            ) : (
              <span>Scan runs on the server across academic, finance and HR. System Administrator only.</span>
            )}
          </div>
          <AccentButton
            icon={runningIntegrity ? Loader2 : RefreshCw}
            onClick={runIntegrity}
            disabled={runningIntegrity}
          >
            {runningIntegrity ? 'Scanning…' : 'Run check'}
          </AccentButton>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <SoftCard pad="p-4">
            <CheckCircle2 size={18} className="text-emerald-500" />
            <p className="text-2xl font-bold mt-2">{recordsScanned}</p>
            <p className="text-xs text-gray-500">Records scanned</p>
          </SoftCard>
          <SoftCard pad="p-4">
            <AlertTriangle size={18} className="text-amber-500" />
            <p className="text-2xl font-bold mt-2">{warnCount}</p>
            <p className="text-xs text-gray-500">Warnings</p>
          </SoftCard>
          <SoftCard pad="p-4">
            <ShieldAlert size={18} className="text-red-500" />
            <p className="text-2xl font-bold mt-2">{errorCount}</p>
            <p className="text-xs text-gray-500">Errors</p>
          </SoftCard>
        </div>

        {/* Named checks (registry) — green/red, drill-down, CSV */}
        {checks.length > 0 && (
          <SoftCard pad="p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                <Shield size={15} className="text-primary" /> Checks
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 text-xs">
                  <Filter size={13} className="text-gray-400" />
                  {(['all', 'Academic', 'Finance', 'HR', 'Drift'] as const).map((c) => (
                    <button
                      key={c}
                      onClick={() => setCatFilter(c)}
                      className={`px-2 py-1 rounded-md font-medium transition-colors ${
                        catFilter === c ? 'bg-primary/10 text-primary' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}>
                      {c === 'all' ? 'All' : c}
                    </button>
                  ))}
                </div>
                <button
                  onClick={exportFailuresCsv}
                  disabled={visibleChecks.every((c) => c.failures.length === 0)}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-primary hover:underline disabled:opacity-40 disabled:no-underline">
                  <FileDown size={13} /> Export CSV
                </button>
              </div>
            </div>

            {visibleChecks.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No checks in this category.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {visibleChecks.map((c) => {
                  const open = expanded.has(c.id);
                  const fc = c.failures.length;
                  return (
                    <li key={c.id}>
                      <button
                        onClick={() => fc > 0 && toggleExpand(c.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left ${
                          fc > 0 ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer' : 'cursor-default'
                        }`}>
                        {c.passed ? (
                          <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                        ) : c.severity === 'error' ? (
                          <ShieldAlert size={16} className="text-red-500 shrink-0" />
                        ) : (
                          <AlertTriangle size={16} className="text-amber-500 shrink-0" />
                        )}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide shrink-0 ${DOMAIN_BADGE[DOMAIN_FROM_CAT[c.category]] ?? 'bg-gray-100 text-gray-600'}`}>
                          {c.category}
                        </span>
                        <span className="font-medium text-gray-800 dark:text-gray-200">{c.label}</span>
                        <span className="text-xs text-gray-400 ml-auto shrink-0">
                          {c.scanned} scanned · {fc === 0 ? 'all clear' : `${fc} failing`}
                        </span>
                        {fc > 0 &&
                          (open ? (
                            <ChevronDown size={15} className="text-gray-400 shrink-0" />
                          ) : (
                            <ChevronRight size={15} className="text-gray-400 shrink-0" />
                          ))}
                      </button>
                      {open && fc > 0 && (
                        <ul className="bg-gray-50/60 dark:bg-gray-900/30 px-4 pb-3">
                          {c.failures.map((f, i) => (
                            <li
                              key={`${c.id}-${i}`}
                              className="flex items-start gap-2 py-1.5 text-xs border-t border-gray-100 dark:border-gray-800 first:border-t-0">
                              {(f.severity ?? c.severity) === 'error' ? (
                                <ShieldAlert size={13} className="text-red-500 mt-0.5 shrink-0" />
                              ) : (
                                <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
                              )}
                              <span className="font-mono text-[11px] text-gray-500 shrink-0">{f.ref}</span>
                              <span className="text-gray-700 dark:text-gray-300">{f.detail}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SoftCard>
        )}

        {/* Cross-reference findings (legacy inline checks) */}
        <SoftCard pad="p-0">
          {runningIntegrity && !integrity ? (
            <div className="py-12"><Spinner label="Scanning data…" /></div>
          ) : !integrity ? (
            <div className="text-center py-12">
              <Shield size={28} className="mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-300">Run a check to scan for data-integrity issues.</p>
            </div>
          ) : filteredLegacy.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 size={28} className="mx-auto mb-2 text-emerald-500" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {catFilter === 'all'
                  ? 'No cross-reference issues found. Data looks healthy.'
                  : 'No cross-reference issues in this category.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredLegacy.map((i, idx) => (
                <li key={`${i.code ?? 'x'}-${idx}`} className="px-4 py-3 flex items-start gap-3 text-sm">
                  {i.level === 'error'
                    ? <ShieldAlert size={16} className="text-red-500 mt-0.5 shrink-0" />
                    : <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide shrink-0 ${DOMAIN_BADGE[i.domain] ?? 'bg-gray-100 text-gray-600'}`}>
                    {DOMAIN_LABEL[i.domain] ?? i.domain}
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{i.text}</span>
                </li>
              ))}
            </ul>
          )}
        </SoftCard>
      </>
    );
  }

  function renderBackups() {
    return (
      <div className="space-y-6">
        {/* Scoped download cards (only the scopes this role can access) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCards.map((card) => {
            const Icon = card.icon;
            const busy = downloadingScope === card.key;
            const label = DOMAIN_LABEL[card.key] ?? card.key;
            return (
              <SoftCard key={card.key} pad="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={18} className="text-primary" />
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white">{label} Backup</h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">{card.desc}</p>
                <AccentButton
                  icon={busy ? Loader2 : Download}
                  onClick={() => downloadScopedBackup(card.key)}
                  disabled={downloadingScope !== null}
                >
                  {busy ? 'Preparing…' : 'Download'}
                </AccentButton>
              </SoftCard>
            );
          })}
        </div>

        {/* Automated archives */}
        <SoftCard>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={18} className="text-primary" />
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Automated Archives</h3>
          </div>
          <div className="text-sm text-gray-500 space-y-2">
            <p>
              A full system archive runs automatically twice daily — 01:00 and 13:00 (Africa/Gaborone) —
              as scheduled cloud functions, stored in Firebase Storage with 30-day retention. These always run.
            </p>
            <p>
              Minute-level recovery is covered separately by Firestore Point-in-Time Recovery (7 days).
              For an off-site copy, download a Full System Backup periodically and keep it on the school server.
            </p>
          </div>

          {/* Extra intraday backups toggle */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Extra intraday backups</p>
                <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                  Additional full backups at <b>06:00, 09:00, 12:00, 15:00, 18:00 and 21:00</b> — dense restore
                  points for busy periods (deadline days, last-minute mark entry). Kept 7 days. Turn OFF during
                  quiet periods; the twice-daily 01:00/13:00 archives keep running either way.
                </p>
              </div>
              <button
                role="switch"
                aria-checked={intradayOn}
                disabled={savingIntraday}
                onClick={() => toggleIntraday(!intradayOn)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                  intradayOn ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${intradayOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <p className={`text-xs font-medium mt-2 ${intradayOn ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}`}>
              {savingIntraday ? 'Saving…' : intradayOn ? 'Intraday backups are ON (every 3 hours, 06:00–21:00)' : 'Intraday backups are OFF — only the 01:00 & 13:00 archives run'}
            </p>
          </div>
        </SoftCard>

        {/* Backup history */}
        <SoftCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white flex items-center gap-2">
              <RefreshCw size={16} className="text-primary" /> Backup History
            </h3>
            <div className="flex items-center gap-3">
              {isFullAdmin && (
                <button onClick={pruneOldest} className="text-xs text-rose-600 hover:underline">Delete oldest…</button>
              )}
              <button onClick={loadBackupHistory} className="text-xs text-primary hover:underline">Refresh</button>
            </div>
          </div>
          {loadingHistory ? (
            <Spinner label="Loading history…" />
          ) : backupHistory.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No backups recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {backupHistory.map((b) => {
                const typeLabel = b.type === 'scoped'
                  ? `${DOMAIN_LABEL[b.scope ?? ''] ?? b.scope ?? 'Scoped'} backup`
                  : `${b.type} backup`;
                return (
                  <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      {b.type === 'manual' && <Download size={14} className="text-primary" />}
                      {b.type === 'scheduled' && <Cloud size={14} className="text-emerald-500" />}
                      {b.type === 'scoped' && <Download size={14} className="text-indigo-500" />}
                      {b.type === 'local' && <HardDrive size={14} className="text-indigo-500" />}
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-white capitalize">{typeLabel}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(b.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {b.sizeEstimate && ` · ${b.sizeEstimate}`}
                          {b.totalRecords ? ` · ${b.totalRecords} records` : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      b.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {b.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </SoftCard>
      </div>
    );
  }
}
